import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ProjectInquirySystem = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    projectType: '',
    budget: '',
    timeline: '',
    industry: '',
    challenges: [],
    companySize: '',
    contactInfo: {
      name: '',
      email: '',
      phone: '',
      company: '',
      title: ''
    }
  });

  const projectTypes = [
    { value: 'digital-transformation', label: 'Digital Transformation' },
    { value: 'process-optimization', label: 'Process Optimization' },
    { value: 'technology-integration', label: 'Technology Integration' },
    { value: 'strategic-consulting', label: 'Strategic Consulting' },
    { value: 'change-management', label: 'Change Management' },
    { value: 'custom-solution', label: 'Custom Solution Development' }
  ];

  const budgetRanges = [
    { value: '50k-100k', label: '$50K - $100K' },
    { value: '100k-250k', label: '$100K - $250K' },
    { value: '250k-500k', label: '$250K - $500K' },
    { value: '500k-1m', label: '$500K - $1M' },
    { value: '1m+', label: '$1M+' },
    { value: 'discuss', label: 'Prefer to discuss' }
  ];

  const timelineOptions = [
    { value: 'immediate', label: 'Immediate (1-2 months)' },
    { value: 'short', label: 'Short-term (3-6 months)' },
    { value: 'medium', label: 'Medium-term (6-12 months)' },
    { value: 'long', label: 'Long-term (12+ months)' },
    { value: 'flexible', label: 'Flexible timeline' }
  ];

  const industries = [
    { value: 'technology', label: 'Technology' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'finance', label: 'Financial Services' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'education', label: 'Education' },
    { value: 'government', label: 'Government' },
    { value: 'other', label: 'Other' }
  ];

  const companySizes = [
    { value: 'startup', label: 'Startup (1-50 employees)' },
    { value: 'small', label: 'Small Business (51-200 employees)' },
    { value: 'medium', label: 'Medium Business (201-1000 employees)' },
    { value: 'large', label: 'Large Enterprise (1000+ employees)' }
  ];

  const challenges = [
    'Legacy system modernization',
    'Process inefficiencies',
    'Digital adoption barriers',
    'Data integration issues',
    'Scalability concerns',
    'Compliance requirements',
    'Team productivity',
    'Customer experience gaps'
  ];

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  const updateFormData = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleChallengeChange = (challenge, checked) => {
    setFormData(prev => ({
      ...prev,
      challenges: checked
        ? [...prev.challenges, challenge]
        : prev.challenges.filter(c => c !== challenge)
    }));
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Project Details</h3> {/* Changed to darker shade */}
              <p className="text-gray-700 mb-6">Help us understand your project requirements</p> {/* Changed to darker shade */}
            </div>

            <Select
              label="What type of project are you considering?"
              options={projectTypes}
              value={formData.projectType}
              onChange={(value) => updateFormData('projectType', value)}
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                label="Budget Range"
                options={budgetRanges}
                value={formData.budget}
                onChange={(value) => updateFormData('budget', value)}
                required
              />

              <Select
                label="Timeline"
                options={timelineOptions}
                value={formData.timeline}
                onChange={(value) => updateFormData('timeline', value)}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                label="Industry"
                options={industries}
                value={formData.industry}
                onChange={(value) => updateFormData('industry', value)}
                required
              />

              <Select
                label="Company Size"
                options={companySizes}
                value={formData.companySize}
                onChange={(value) => updateFormData('companySize', value)}
                required
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Current Challenges</h3> {/* Changed to darker shade */}
              <p className="text-gray-700 mb-6">Select the challenges you're currently facing</p> {/* Changed to darker shade */}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {challenges.map((challenge) => (
                <Checkbox
                  key={challenge}
                  label={challenge} // Checkbox component should render its label with appropriate contrast. Assuming it handles text color internally or takes a prop.
                  checked={formData.challenges.includes(challenge)}
                  onChange={(e) => handleChallengeChange(challenge, e.target.checked)}
                />
              ))}
            </div>

            <div className="mt-8">
              <label className="block text-sm font-medium text-gray-800 mb-2"> {/* Changed to darker shade */}
                Additional Details (Optional)
              </label>
              <textarea
                className="w-full px-3 py-2 bg-white/70 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-brand-cyan text-gray-800 placeholder-gray-500 resize-none" // Changed background, border, text, and placeholder colors
                rows={4}
                placeholder="Tell us more about your specific challenges or requirements..."
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Contact Information</h3> {/* Changed to darker shade */}
              <p className="text-gray-700 mb-6">How can we reach you to discuss your project?</p> {/* Changed to darker shade */}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Full Name"
                type="text"
                value={formData.contactInfo.name}
                onChange={(e) => updateFormData('contactInfo.name', e.target.value)}
                required
              />

              <Input
                label="Email Address"
                type="email"
                value={formData.contactInfo.email}
                onChange={(e) => updateFormData('contactInfo.email', e.target.value)}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Phone Number"
                type="tel"
                value={formData.contactInfo.phone}
                onChange={(e) => updateFormData('contactInfo.phone', e.target.value)}
                required
              />

              <Input
                label="Job Title"
                type="text"
                value={formData.contactInfo.title}
                onChange={(e) => updateFormData('contactInfo.title', e.target.value)}
                required
              />
            </div>

            <Input
              label="Company Name"
              type="text"
              value={formData.contactInfo.company}
              onChange={(e) => updateFormData('contactInfo.company', e.target.value)}
              required
            />

            <div className="bg-white/70 backdrop-blur-sm border border-gray-300 p-4 rounded-lg"> {/* Changed background and border */}
              <h4 className="font-semibold text-gray-800 mb-2">What happens next?</h4> {/* Changed to darker shade */}
              <ul className="space-y-2 text-sm text-gray-700"> {/* Changed to darker shade */}
                <li className="flex items-center">
                  <Icon name="Clock" size={16} className="mr-2 text-brand-cyan" />
                  We'll review your inquiry within 2-4 hours
                </li>
                <li className="flex items-center">
                  <Icon name="User" size={16} className="mr-2 text-brand-cyan" />
                  A practice leader will reach out to discuss your needs
                </li>
                <li className="flex items-center">
                  <Icon name="Calendar" size={16} className="mr-2 text-brand-cyan" />
                  We'll schedule a consultation call at your convenience
                </li>
              </ul>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-secondary/20 to-primary/20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-heading"> {/* Changed to darker shade */}
            Intelligent Project Inquiry
          </h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto"> {/* Changed to darker shade */}
            Our smart inquiry system helps us understand your needs and connect you with the right experts.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-800">Step {currentStep} of 3</span> {/* Changed to darker shade */}
            <span className="text-sm text-gray-600">{Math.round((currentStep / 3) * 100)}% Complete</span> {/* Changed to darker shade */}
          </div>
          <div className="w-full bg-gray-300 rounded-full h-2"> {/* Changed from white/20 to darker gray */}
            <div
              className="bg-brand-cyan h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / 3) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Form Content */}
        <div className="glass-morph bg-white/90 backdrop-blur-md border border-gray-300 rounded-xl p-8 shadow-sm"> {/* Changed background and border */}
          {renderStep()}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-300"> {/* Changed border */}
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 1}
            >
              <Icon name="ArrowLeft" size={20} className="mr-2" />
              Previous
            </Button>

            {currentStep < 3 ? (
              <Button
                variant="default"
                onClick={handleNext}
              >
                Next Step
                <Icon name="ArrowRight" size={20} className="ml-2" />
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={handleSubmit}
                className="bg-success hover:bg-success/90"
              >
                Submit Inquiry
                <Icon name="Send" size={20} className="ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectInquirySystem;